import { Box, Paper } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { CalendarIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import Highcharts from "highcharts"
import HighchartsReact from "highcharts-react-official"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { IDashboard } from "../../../models/Dashboard"
import { CardHeader } from "./CardHeader"
import classes from "../real.module.css"

interface Props {
  byInterval: IDashboard["byInterval"] | undefined
  isBuyer: boolean
  startDate: string | null
  endDate: string | null
  onStartDateChange: (next: string | null) => void
  onEndDateChange: (next: string | null) => void
}

export function SalesChartCard({
  byInterval,
  isBuyer,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}: Props) {
  const { t } = useTranslation(["dashboard"])

  const options = useMemo(
    () => buildChartOptions(byInterval ?? [], {
      orderAmount: t("dashboard:chart.orderAmount"),
      deliveredAmount: t("dashboard:chart.deliveredAmount"),
      orderCount: t("dashboard:chart.orderCount"),
      million: t("dashboard:numberFormat.million"),
    }),
    [byInterval, t],
  )

  return (
    <Paper className={classes.card}>
      <CardHeader
        title={t(isBuyer ? "dashboard:purchaseOrderTitle" : "dashboard:salesOrderTitle")}
        subtitle={`${t(isBuyer ? "dashboard:subtitleBuyer" : "dashboard:subtitle")} · ${t("dashboard:chart.cumulative")}`}
        right={
          <DatePickerInput
            type="range"
            leftSection={<CalendarIcon size={16} />}
            valueFormat="YYYY-MM-DD"
            value={[startDate ? dayjs(startDate).toDate() : null, endDate ? dayjs(endDate).toDate() : null]}
            onChange={(e) => {
              onStartDateChange(e[0] ? dayjs(e[0]).format("YYYY-MM-DD") : null)
              onEndDateChange(e[1] ? dayjs(e[1]).format("YYYY-MM-DD") : null)
            }}
            aria-label={t("dashboard:chart.range")}
          />
        }
      />
      <Box className={classes.legend}>
        <Box className={classes.legendItem}>
          <Box className={classes.legendSwatch} style={{ background: SERIES.order }} />
          {t("dashboard:chart.orderAmount")}
        </Box>
        <Box className={classes.legendItem}>
          <Box className={classes.legendSwatch} style={{ background: SERIES.delivered }} />
          {t("dashboard:chart.deliveredAmount")}
        </Box>
        <Box className={classes.legendItem}>
          <Box className={classes.legendLine} style={{ background: SERIES.count }} />
          {t("dashboard:chart.orderCount")}
        </Box>
      </Box>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Paper>
  )
}

const SERIES = {
  order: "var(--mantine-color-dark-8)",
  delivered: "var(--mantine-color-gray-5)",
  count: "var(--mantine-color-brand-6)",
}

function buildChartOptions(
  byInterval: NonNullable<IDashboard["byInterval"]>,
  labels: { orderAmount: string; deliveredAmount: string; orderCount: string; million: string },
) {
  return {
    chart: {
      height: 300,
      style: { fontFamily: "inherit" },
      backgroundColor: "transparent",
    },
    title: { text: "" },
    xAxis: [{
      categories: byInterval.map((item) => item?.date),
      crosshair: true,
      labels: { style: { color: "var(--mantine-color-gray-7)", fontSize: "14px" } },
      lineColor: "var(--mantine-color-gray-3)",
    }],
    yAxis: [
      {
        labels: {
          formatter(this: { value: number | string }) {
            const v = Number(this.value)
            return v >= 1_000_000 ? `${v / 1_000_000} ${labels.million}` : String(v)
          },
          style: { color: "var(--mantine-color-gray-7)", fontSize: "14px" },
        },
        title: { text: "" },
        gridLineColor: "var(--mantine-color-gray-2)",
        allowDecimals: false,
      },
      {
        title: { text: "" },
        labels: { format: "{value}", style: { color: "var(--mantine-color-gray-7)", fontSize: "14px" } },
        opposite: true,
        gridLineWidth: 0,
        allowDecimals: false,
      },
    ],
    tooltip: {
      shared: true,
      valueDecimals: 2,
      backgroundColor: "var(--mantine-color-white)",
      borderColor: "var(--mantine-color-gray-3)",
      borderRadius: 8,
    },
    legend: { enabled: false },
    plotOptions: {
      column: { borderRadius: 2, pointPadding: 0.1, groupPadding: 0.1, maxPointWidth: 14 },
      spline: { lineWidth: 2, marker: { enabled: false } },
    },
    series: [
      {
        name: labels.orderAmount,
        color: SERIES.order,
        type: "column" as const,
        data: byInterval.map((item) => item?.orderAmount || 0),
        yAxis: 0,
      },
      {
        name: labels.deliveredAmount,
        type: "column" as const,
        color: SERIES.delivered,
        data: byInterval.map((item) => item?.deliveredAmount || 0),
        yAxis: 0,
      },
      {
        name: labels.orderCount,
        type: "spline" as const,
        color: SERIES.count,
        data: byInterval.map((item) => item?.orderCount || 0),
        yAxis: 1,
      },
    ],
    accessibility: { enabled: false },
    credits: { enabled: false },
  }
}
